import * as yup from 'yup';

declare module 'yup' {
  interface StringSchema {
    draftableRequired(): this;
    empty(...emptyValues: unknown[]): this;
  }

  interface DateSchema {
    draftableRequired(): this;
  }

  interface NumberSchema {
    draftableRequired(): this;
    draftableMin(min: number): this;
    draftablePositive(): this;
    empty(...emptyValues: unknown[]): this;
  }

  interface ArraySchema<TIn, TContext, TDefault, TFlags> {
    draftableRequired(): this;
    draftableMin<T extends ArraySchema<TIn, TContext, TDefault, TFlags>>(
      this: T,
      min: number,
      message?: yup.Message<{ min: number }>
    ): T;

    draftableMinOneAsRequired<T extends ArraySchema<TIn, TContext, TDefault, TFlags>>(this: T): T;
  }
}

/**
 * Makes required field optional in draft mode.
 */
function draftableRequired<T extends yup.AnySchema>(this: T): T {
  return this.when('$isDraftMode', {
    is: true,
    then: (schema) => schema.nullable(),
    otherwise: (schema) => schema.nonNullable().required(),
  });
}

yup.addMethod(yup.number, 'draftableRequired', draftableRequired);
yup.addMethod(yup.string, 'draftableRequired', draftableRequired);
yup.addMethod(yup.date, 'draftableRequired', draftableRequired);
yup.addMethod(yup.array, 'draftableRequired', draftableRequired);

/**
 * Makes the required field optional in draft mode and allows an empty array
 */
yup.addMethod(yup.array, 'draftableMin', function (min: number, message?: yup.Message<{ min: number }>) {
  return this.when('$isDraftMode', {
    is: true,
    then: (schema) => schema.min(0).nullable(),
    otherwise: (schema) => schema.min(min, message).nonNullable(),
  });
});

/**
 * Makes positive number validation optional in draft mode.
 */
yup.addMethod(yup.number, 'draftablePositive', function () {
  return this.when('$isDraftMode', {
    is: true,
    then: (schema) => schema.min(0), // no point in allowing negative numbers in draft mode
    otherwise: (schema) => schema.positive(),
  });
});

/**
 * Applies draftableMin(1) with a custom error message.
 * It'll produce "foo is required" error message instead of "foo must contain at least 1 item"
 * Meant to be used with file uploader which require a single file
 */
yup.addMethod(yup.array, 'draftableMinOneAsRequired', function () {
  return this.draftableMin(1, 'VALIDATION_ERROR_FIELD_REQUIRED');
});

/**
 * Accepts a list of additional values that should be considered as empty (undefined)
 * It's useful because otherwise number() would validate nulls and empty strings with "foo not a number" error,
 * and by treating them as empty, we can avoid that and display a more user-friendly message ("foo is required")
 */
yup.addMethod(yup.number, 'empty', function (emptyValues: unknown[]) {
  return this.transform((value, originalValue) => {
    if (emptyValues.includes(originalValue)) {
      return undefined;
    }

    return value;
  });
});

/**
 * Ensures a number is at least a minimum value.
 * Similar to min() but with better type error handling.
 */
yup.addMethod(yup.number, 'positiveMin', function (min: number, message?: yup.Message<{ min: number }>) {
  return this.typeError('VALIDATION_ERROR_FIELD_MUST_BE_A_NUMBER').min(
    min,
    message || 'VALIDATION_ERROR_FIELD_MUST_BE_AT_LEAST_X'
  );
});
